/**
 * Search & Browse — free, instant, no-AI lookup over Compass's code/schema
 * index (methods, stored procedures, tables, fields, XHTML, VO, Ctrl) plus a
 * Module → App → Project → Class → Method drill-down with counts.
 * Contract + real samples: Compass repo exports/lisa-search-browse-2026-09-26.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Code2, Database, Info, ListTree, Loader2, Search } from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { CodeMapPanel } from './CodeMapPanel';
import {
  BrowseItem,
  BrowseSelection,
  MethodSource,
  ProcSource,
  SEARCH_KINDS,
  SearchItem,
  SearchResponse,
  browseChain,
  explainField,
  explainProc,
  explainTable,
  getMethodSource,
  getProcSource,
  searchNames,
} from '../../services/compassApi';

interface Props {
  /** Ticket modules — offered as one-click search chips. */
  modules?: string[];
}

const PER_KIND = 8;

function errorText(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  return /Compass (404|405|501)/.test(msg)
    ? 'Not available on Compass yet.'
    : `Lookup failed (${msg}).`;
}

/** Search keys look like "Method:<file path>:<Class.method#n>", "Class:<path>:<Class>",
 *  or a bare file path (xhtml). */
function splitKey(key: string): { path: string; symbol: string | null } {
  const m = key.match(/^(?:Method|Class):(.+?\.java)(?::(.+))?$/);
  if (m) return { path: m[1], symbol: m[2] ?? null };
  return { path: key, symbol: null };
}

/** Lazy source viewer for one method key. */
function SourceBlock({ itemKey }: { itemKey: string }) {
  const [src, setSrc] = useState<MethodSource | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getMethodSource(itemKey)
      .then((s) => { if (!cancelled) setSrc(s); })
      .catch((e) => { if (!cancelled) setErr(errorText(e)); });
    return () => { cancelled = true; };
  }, [itemKey]);

  if (err) return <p className="text-xs text-red-700 mt-1">{err}</p>;
  if (!src) {
    return (
      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" /> Loading source…
      </p>
    );
  }
  const lines = src.start_line != null
    ? `${src.start_line}${src.end_line != null && src.end_line !== src.start_line ? `–${src.end_line}` : ''}`
    : '';
  return (
    <div className="mt-1">
      {src.resolved_note && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1">
          {src.resolved_note}
        </p>
      )}
      {src.file && (
        <p className="font-mono text-[11px] text-gray-500 break-all mb-1">
          {src.file}{lines ? `:${lines}` : ''}
        </p>
      )}
      {src.content == null ? (
        <p className="text-xs text-gray-600">{src.error ?? 'No source returned.'}</p>
      ) : (
        <PagedCode content={src.content} firstLine={src.start_line ?? 1} />
      )}
      {src.truncated && (
        <p className="text-[11px] text-amber-800 mt-1">
          Compass truncated this source; the rest is not available here.
        </p>
      )}
    </div>
  );
}

const CODE_PAGE = 100;

/** Numbered code, 100 lines at a time — the whole text is already loaded. */
function PagedCode({ content, firstLine }: { content: string; firstLine: number }) {
  const lines = content.split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  const [shown, setShown] = useState(CODE_PAGE);
  const visible = Math.min(shown, lines.length);
  const width = String(firstLine + lines.length - 1).length;
  const last = firstLine + lines.length - 1;
  return (
    <div>
      <pre className="text-xs font-mono bg-gray-900 text-gray-100 rounded p-2 overflow-x-auto max-h-96 overflow-y-auto">
        {lines.slice(0, visible).map((l, i) => (
          <div key={i}>
            <span className="text-gray-500 select-none">{String(firstLine + i).padStart(width, ' ')}  </span>
            {l}
          </div>
        ))}
      </pre>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-600">
        <span>Lines {firstLine}–{firstLine + visible - 1} of {firstLine}–{last}</span>
        {visible < lines.length && (
          <>
            <button
              onClick={() => setShown((n) => n + CODE_PAGE)}
              className="px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              Show next {Math.min(CODE_PAGE, lines.length - visible)} lines
            </button>
            <button
              onClick={() => setShown(lines.length)}
              className="px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            >
              Show all
            </button>
          </>
        )}
        {visible > CODE_PAGE && (
          <button
            onClick={() => setShown(CODE_PAGE)}
            className="px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
          >
            Collapse
          </button>
        )}
      </div>
    </div>
  );
}

const EXPLAIN: Record<string, (key: string) => Promise<{ explanation: string }>> = {
  procedure: explainProc,
  table: explainTable,
  field: explainField,
};

/** Free, deterministic explanation of a procedure, table or field. */
function DetailsBlock({ kind, itemKey }: { kind: string; itemKey: string }) {
  const [text, setText] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EXPLAIN[kind](itemKey)
      .then((r) => { if (!cancelled) setText(r.explanation); })
      .catch((e) => { if (!cancelled) setErr(errorText(e)); });
    return () => { cancelled = true; };
  }, [kind, itemKey]);

  if (err) return <p className="text-xs text-red-700 mt-1">{err}</p>;
  if (text === null) {
    return (
      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" /> Loading details…
      </p>
    );
  }
  return (
    <div className="mt-2 border-t border-emerald-100 pt-2 text-sm">
      <MarkdownRenderer content={text} showTableOfContents={false} />
    </div>
  );
}

function ProcSourceBlock({ itemKey }: { itemKey: string }) {
  const [src, setSrc] = useState<ProcSource | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getProcSource(itemKey)
      .then((s) => { if (!cancelled) setSrc(s); })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [itemKey]);

  if (err) return <p className="text-xs text-red-700 mt-1">{err}</p>;
  if (!src) {
    return (
      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" /> Loading source…
      </p>
    );
  }
  return (
    <div className="mt-1">
      {src.content == null ? (
        <p className="text-xs text-gray-600">No source returned.</p>
      ) : (
        <PagedCode content={src.content} firstLine={1} />
      )}
      {src.truncated && (
        <p className="text-[11px] text-amber-800 mt-1">
          Compass truncated this source ({src.real_chars?.toLocaleString() ?? '?'} characters in full).
        </p>
      )}
    </div>
  );
}

function ResultCard({ kind, item }: { kind: string; item: SearchItem }) {
  const [showSource, setShowSource] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const canShowDetails = kind in EXPLAIN;
  const { path: keyPath, symbol } = splitKey(item.key);
  const path = item.file ?? keyPath;
  const range = item.line_start != null
    ? `:${item.line_start}${item.line_end != null && item.line_end !== item.line_start ? `–${item.line_end}` : ''}`
    : '';
  const canShowSource = kind === 'method' || kind === 'procedure';
  const btn = 'flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50';
  return (
    <div className="border border-emerald-200 rounded bg-white p-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm text-emerald-900 break-all">{symbol ?? item.name}</div>
          <div className="font-mono text-[11px] text-gray-500 break-all">
            {item.app ? `[${item.app}] ` : ''}{path}{range}
          </div>
        </div>
        <div className="shrink-0 flex gap-1">
          {canShowDetails && (
            <button onClick={() => setShowDetails((v) => !v)} className={btn}>
              <Info size={12} /> {showDetails ? 'Hide details' : 'View details'}
            </button>
          )}
          {canShowSource && (
            <button onClick={() => setShowSource((v) => !v)} className={btn}>
              <Code2 size={12} /> {showSource ? 'Hide source' : 'View source'}
            </button>
          )}
          {canShowSource && (
            <button onClick={() => setShowMap((v) => !v)} className={btn}>
              <ListTree size={12} /> {showMap ? 'Hide map' : 'View map'}
            </button>
          )}
        </div>
      </div>
      {item.edge_note && <p className="text-xs italic text-gray-600 mt-1">{item.edge_note}</p>}
      {showDetails && canShowDetails && <DetailsBlock kind={kind} itemKey={item.key} />}
      {showSource && kind === 'method' && <SourceBlock itemKey={item.key} />}
      {showSource && kind === 'procedure' && <ProcSourceBlock itemKey={item.key} />}
      {showMap && canShowSource && <CodeMapPanel kind={kind === 'method' ? 'method' : 'procedure'} itemKey={item.key} />}
    </div>
  );
}

const LEVELS = ['module', 'app', 'project', 'class', 'method'] as const;
type Level = (typeof LEVELS)[number];
const LEVEL_TITLE: Record<Level, string> = {
  module: 'Module', app: 'App', project: 'Project', class: 'Class', method: 'Method',
};
const LEVEL_HINT: Record<Level, string> = {
  module: 'Loading…', app: 'Pick a module', project: 'Pick an app', class: 'Pick a project', method: 'Pick a class',
};

export function CompassSearchBrowse({ modules = [] }: Props) {
  const [open, setOpen] = useState(false);

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState<string | null>(null);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [onlyApp, setOnlyApp] = useState(false);

  const [sel, setSel] = useState<BrowseSelection>({});
  const [items, setItems] = useState<Record<Level, BrowseItem[] | null>>({
    module: null, app: null, project: null, class: null, method: null,
  });
  const [browseErr, setBrowseErr] = useState<string | null>(null);
  const [browseNote, setBrowseNote] = useState<string | null>(null);
  const [openMethod, setOpenMethod] = useState<string | null>(null);
  const browseSeq = useRef(0);
  const searchSeq = useRef(0);

  const runSearch = useCallback(async (q: string, app: string | null) => {
    if (!q.trim()) return;
    const seq = ++searchSeq.current;
    setSearching(true);
    setSearchErr(null);
    try {
      const r = await searchNames(q.trim(), { perKind: PER_KIND, app });
      if (seq === searchSeq.current) setResponse(r);
    } catch (e) {
      if (seq === searchSeq.current) {
        setSearchErr(errorText(e));
        setResponse(null);
      }
    } finally {
      if (seq === searchSeq.current) setSearching(false);
    }
  }, []);

  const loadLevel = useCallback(async (level: Level, next: BrowseSelection) => {
    const seq = ++browseSeq.current;
    setBrowseErr(null);
    try {
      const r = await browseChain(next);
      if (seq !== browseSeq.current) return;
      setItems((cur) => ({ ...cur, [level]: r.items ?? [] }));
      if (r.note) setBrowseNote(r.note);
    } catch (e) {
      if (seq === browseSeq.current) setBrowseErr(errorText(e));
    }
  }, []);

  useEffect(() => {
    if (open && items.module === null) void loadLevel('module', {});
  }, [open, items.module, loadLevel]);

  const pick = (level: Level, value: string) => {
    const idx = LEVELS.indexOf(level);
    const next: BrowseSelection = { ...sel };
    LEVELS.slice(idx, 4).forEach((l) => { next[l as keyof BrowseSelection] = l === level ? value : null; });
    setSel(next);
    setOpenMethod(null);
    setItems((cur) => {
      const cleared = { ...cur };
      LEVELS.slice(idx + 1).forEach((l) => { cleared[l] = null; });
      return cleared;
    });
    if (level === 'module') setOnlyApp(false);
    void loadLevel(LEVELS[idx + 1], next);
    if (level === 'app' && onlyApp && query.trim()) void runSearch(query, value);
  };

  const scopedApp = onlyApp ? sel.app ?? null : null;
  const submit = () => void runSearch(query, scopedApp);

  return (
    <div className="border border-emerald-300 rounded-lg bg-emerald-50/40 mb-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left"
      >
        <Database size={16} className="text-emerald-700" />
        <span className="font-semibold text-sm text-emerald-900">
          Search &amp; Browse: Method, SP, Table, Fields
        </span>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
          free, instant, no AI call
        </span>
        <span className="ml-auto text-emerald-700">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {modules.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-emerald-900 mb-1">Suggested from this ticket:</div>
              <div className="flex flex-wrap gap-1.5">
                {modules.map((m) => (
                  <button
                    key={m}
                    onClick={() => { setQuery(m); void runSearch(m, scopedApp); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-emerald-400 text-emerald-800 bg-white hover:bg-emerald-50"
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder="Search methods, procedures, tables, fields, screens…"
              className="flex-1 border border-emerald-300 rounded px-2 py-1.5 text-sm bg-white"
            />
            <button
              onClick={submit}
              disabled={searching || !query.trim()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
            </button>
          </div>
          <p className="text-[11px] text-gray-500">
            e.g. <code>hand%notes</code> (any characters in between) · <code>vesse_master</code> (exactly one
            character; type <code>\_</code> for a literal underscore) · <code>invoice header</code> or{' '}
            <code>invoice AND header</code> (both words, any order)
          </p>

          {sel.app && (
            <label className="flex items-center gap-1.5 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={onlyApp}
                onChange={(e) => {
                  setOnlyApp(e.target.checked);
                  if (query.trim()) void runSearch(query, e.target.checked ? sel.app ?? null : null);
                }}
              />
              Only in app <span className="font-mono">{sel.app}</span> (hides matches from other apps)
            </label>
          )}

          {searchErr && <p className="text-xs text-red-700">{searchErr}</p>}

          {response && !searchErr && (
            <div className="space-y-3">
              {response.terms && response.terms.length > 0 && (
                <p className="text-xs text-emerald-900">
                  Matching {response.terms.length > 1 ? 'all of' : ''}{' '}
                  {response.terms.map((t) => (
                    <code key={t} className="px-1 mx-0.5 bg-white border border-emerald-200 rounded">{t}</code>
                  ))}
                  {response.terms.length > 1 ? ' in any order' : ''} — up to {PER_KIND} per category shown
                </p>
              )}
              {SEARCH_KINDS.map(({ kind, label }) => {
                const found = response.results?.[kind] ?? [];
                const count = response.counts?.[kind];
                if (found.length === 0 && !(count && count.total > 0)) return null;
                return (
                  <div key={kind}>
                    <div className="text-xs font-semibold tracking-wide uppercase text-emerald-900 mb-1">
                      {label} ({count?.total ?? found.length})
                    </div>
                    <div className="space-y-1.5">
                      {found.map((it, i) => (
                        <ResultCard key={`${it.key}-${i}`} kind={kind} item={it} />
                      ))}
                    </div>
                    {count && count.missing > 0 && (
                      <p className="text-[11px] text-gray-500 mt-1">
                        +{count.missing} more not shown — refine the search to narrow it.
                      </p>
                    )}
                  </div>
                );
              })}
              {Object.values(response.counts ?? {}).every((c) => !c || c.total === 0) && (
                <p className="text-xs text-gray-500">No matches.</p>
              )}
            </div>
          )}

          <div>
            {browseErr && <p className="text-xs text-red-700 mb-1">{browseErr}</p>}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {LEVELS.map((level, idx) => {
                const list = items[level];
                const selected = idx < 4 ? sel[level as keyof BrowseSelection] : openMethod;
                const enabled = idx === 0 || items[LEVELS[idx - 1]] !== null && !!sel[LEVELS[idx - 1] as keyof BrowseSelection];
                return (
                  <div key={level} className="min-w-0">
                    <div className="text-sm font-semibold text-emerald-900 mb-1">{LEVEL_TITLE[level]}</div>
                    <div className="max-h-64 overflow-y-auto rounded bg-emerald-50/60 border border-emerald-100">
                      {list === null ? (
                        <p className="text-xs text-gray-500 p-2">{LEVEL_HINT[level]}</p>
                      ) : list.length === 0 ? (
                        <p className="text-xs text-gray-500 p-2">Nothing here.</p>
                      ) : (
                        list.map((it, i) => {
                          const id = it.key ?? it.value;
                          const active = selected === it.value || selected === id;
                          return (
                            <button
                              key={`${id}-${i}`}
                              disabled={!enabled}
                              onClick={() =>
                                level === 'method'
                                  ? setOpenMethod((cur) => (cur === id ? null : id))
                                  : pick(level, it.value)
                              }
                              className={`w-full text-left px-2 py-1 flex items-baseline justify-between gap-2 ${
                                active ? 'bg-emerald-300 font-medium' : 'hover:bg-emerald-100'
                              }`}
                              title={
                                it.tables != null
                                  ? `${it.count ?? 0} chain rows · ${it.tables} tables · ${it.procedures ?? 0} procedures`
                                  : undefined
                              }
                            >
                              <span className="break-all font-mono text-xs">
                                {level === 'method' ? splitKey(it.value).symbol ?? it.value : it.value}
                              </span>
                              {it.count != null && (
                                <span className="text-[11px] text-gray-500 shrink-0">({it.count})</span>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {openMethod && items.method?.some((x) => x.key === openMethod) && (
              <SourceBlock itemKey={openMethod} />
            )}
            {browseNote && <p className="text-[11px] text-gray-500 mt-2 italic">{browseNote}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
